import {
  Button,
  ButtonGroup,
  Divider,
  FormControl,
  Typography,
} from '@mui/material';
import { Form, FormikHelpers, FormikProvider, useFormik } from 'formik';
import { compact, filter, find, includes, map, sortBy } from 'lodash';
import React, { Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import {
  FormControls,
  FormikCheckbox,
  FormikNumericField,
  HelpTooltip,
  usePostSavePrompt,
  useUnsavedChangesGuard,
} from 'components';
import { MAX_PARALLEL_CUSTOMER_ORDERS_LIMIT } from 'constants/shops.constants';
import { OrderType, PayinOrderTimeoutConfigKey } from 'enums';
import {
  useCrossFiatCurrencyExchange,
  useCurrencies,
  useMeta,
  useUser,
  useUserContext,
} from 'hooks';
import { TranslationNamespace } from 'i18n';
import { AmountAntiFraudRule, Shop } from 'types';
import { shopUtils, validationUtils } from 'utils';

import { AmountAntiFraud } from '../../AmountAntiFraud';
import { MaxParallelCustomerOrdersField } from '../../MaxParallelCustomerOrdersField';
import { PayinBlockAmounts } from '../../PayinBlockAmounts';
import { PayinBlockCustomers } from '../../PayinBlockCustomers';
import { ShopPayinFiatCurrencyExchangeConfigs } from '../../ShopPayinFiatCurrencyExchangeConfigs';
import { ShopPayinGroups } from '../../ShopPayinGroups';
import { ShopPayinTradeMethods } from '../../ShopPayinTradeMethods';
import {
  useManageShop,
  ManageShopAccordion,
  ManageShopTabActions,
} from '../components';
import { useShopSubmit } from '../hooks';

enum AmountRandomizerMode {
  Absolute = 'absolute',
  Percentage = 'percentage',
}

type PayinValues = {
  // Routing
  payinTradeMethodsConfigs: any[];
  payinGroupsConfigs: any[];
  payinFiatCurrencyExchangeConfigs: any[];
  // Amounts
  payinOrderMinAmount: number;
  payinOrderMaxAmount: number;
  maxParallelCustomerOrders: number | null;
  payinAmountAntiFraudRules: AmountAntiFraudRule[];
  payinAmountRandomizer: any;
  // Timeouts
  payinTimeoutConfig?: any;
  payinCreateResponseTimeout: number | null;
  payinCreateSyncRequisitesResponseTimeout: number | null;
  // Requisites & Distribution
  payinDistributionSameRequisites: boolean;
  distributionDebug: boolean;
  payinNSPKInstantSyncRequisites: boolean;
  useProviderPaymentUrl: boolean;
  payinTSBPFallback: boolean;
  payinTCard2CardFallback: boolean;
  payinTSimFallback: boolean;
  payinTSBPNSPKTransform: boolean;
  payinTCard2CardNSPKTransform: boolean;
  payinTNSPKFallback: boolean;
  fullWebhook: boolean;
  payinTrustedCustomersOnly: boolean;
  // Customers
  payinBlockCustomersConfigs: any[];
  payinBlockAmountConfigs: any[];
};

export const PayinTab: React.FC = () => {
  const { shopId, shopData, isNew } = useManageShop();
  const { onSavedSuccess } = usePostSavePrompt();
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.shops.manage',
  });
  const { t: tCommon } = useTranslation();
  const { isAdminOrTechOperator } = useUser();
  const { getFiatCurrencyCode } = useCurrencies();
  const { tradeMethods } = useUserContext();
  const { isMultitransferEnabled } = useMeta();
  const { crossFiatCurrencyExchange } = useCrossFiatCurrencyExchange({
    enabled: true,
    fiatCurrencyId: shopData?.fiatCurrencyId,
  });

  const { handleSubmit: submitShop } = useShopSubmit({
    shopId,
    onSuccess: onSavedSuccess,
  });

  const payinGroups = useMemo(
    () => filter(shopData?.groups, (group) => group.payin),
    [shopData],
  );

  const getSortedPayinGroupsConfigs = useCallback((shop: Shop) => {
    const payinGroupsFiltered = filter(shop.groups, (group) => group.payin);
    const groupsWithPriority = payinGroupsFiltered.map((group) => {
      const config = find(
        shop.payinGroupsConfigs,
        (config) => config.groupId === group.id,
      );
      return {
        id: config?.id,
        groupId: group.id,
        priority: config?.priority || 0,
      };
    });
    return sortBy(groupsWithPriority, (group) => -group.priority);
  }, []);

  const initialValues: PayinValues = useMemo(() => {
    if (shopData) {
      return {
        payinTradeMethodsConfigs: shopData.payinTradeMethodsConfigs || [],
        payinGroupsConfigs: getSortedPayinGroupsConfigs(shopData),
        payinFiatCurrencyExchangeConfigs:
          shopData.payinFiatCurrencyExchangeConfigs || [],
        payinOrderMinAmount: shopData.payinOrderMinAmount || 0,
        payinOrderMaxAmount: shopData.payinOrderMaxAmount || 0,
        maxParallelCustomerOrders: shopData.maxParallelCustomerOrders ?? 0,
        payinAmountAntiFraudRules: shopData.payinAmountAntiFraudRules || [],
        payinAmountRandomizer: shopData.payinAmountRandomizer || {
          enabled: false,
        },
        payinTimeoutConfig: shopData.payinTimeoutConfig,
        payinCreateResponseTimeout: shopData.payinCreateResponseTimeout ?? null,
        payinCreateSyncRequisitesResponseTimeout:
          shopData.payinCreateSyncRequisitesResponseTimeout ?? null,
        payinDistributionSameRequisites:
          shopData.payinDistributionSameRequisites || false,
        distributionDebug: shopData.distributionDebug || false,
        payinNSPKInstantSyncRequisites:
          shopData.payinNSPKInstantSyncRequisites || false,
        useProviderPaymentUrl: shopData.useProviderPaymentUrl || false,
        payinTSBPFallback: shopData.payinTSBPFallback || false,
        payinTCard2CardFallback: shopData.payinTCard2CardFallback || false,
        payinTSimFallback: shopData.payinTSimFallback || false,
        payinTSBPNSPKTransform: shopData.payinTSBPNSPKTransform || false,
        payinTCard2CardNSPKTransform:
          shopData.payinTCard2CardNSPKTransform || false,
        payinTNSPKFallback: shopData.payinTNSPKFallback || false,
        fullWebhook: shopData.fullWebhook || false,
        payinTrustedCustomersOnly: shopData.payinTrustedCustomersOnly || false,
        payinBlockCustomersConfigs: shopData.payinBlockCustomersConfigs || [],
        payinBlockAmountConfigs: shopData.payinBlockAmountConfigs || [],
      };
    }
    return {
      payinTradeMethodsConfigs: [],
      payinGroupsConfigs: [],
      payinFiatCurrencyExchangeConfigs: [],
      payinOrderMinAmount: 0,
      payinOrderMaxAmount: 0,
      maxParallelCustomerOrders: null,
      payinAmountAntiFraudRules: [],
      payinAmountRandomizer: { enabled: false },
      payinCreateResponseTimeout: null,
      payinCreateSyncRequisitesResponseTimeout: null,
      payinDistributionSameRequisites: false,
      distributionDebug: false,
      payinNSPKInstantSyncRequisites: false,
      useProviderPaymentUrl: false,
      payinTSBPFallback: false,
      payinTCard2CardFallback: false,
      payinTSimFallback: false,
      payinTSBPNSPKTransform: false,
      payinTCard2CardNSPKTransform: false,
      payinTNSPKFallback: false,
      fullWebhook: false,
      payinTrustedCustomersOnly: false,
      payinBlockCustomersConfigs: [],
      payinBlockAmountConfigs: [],
    };
  }, [shopData, getSortedPayinGroupsConfigs]);

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        payinTradeMethodsConfigs: Yup.array().of(
          Yup.object().shape({
            tradeMethodId: Yup.string().required(tCommon('errors.required')),
            anyBank: Yup.boolean(),
          }),
        ),
        payinGroupsConfigs: Yup.array().of(
          Yup.object().shape({
            groupId: Yup.string().required(tCommon('errors.required')),
          }),
        ),
        ...(isAdminOrTechOperator && {
          payinFiatCurrencyExchangeConfigs: Yup.array().of(
            Yup.object().shape({
              fiatCurrencyExchangeId: Yup.string().required(
                tCommon('errors.required'),
              ),
              minSpread: Yup.number()
                .moreThan(-100, tCommon('errors.percentage'))
                .lessThan(100, tCommon('errors.percentage'))
                .nullable(),
              maxSpread: Yup.number()
                .moreThan(-100, tCommon('errors.percentage'))
                .lessThan(100, tCommon('errors.percentage'))
                .nullable(),
              traderSpreadCompensation: Yup.boolean(),
            }),
          ),
        }),
        payinAmountAntiFraudRules: Yup.array().of(
          Yup.object().shape({
            minAmount: Yup.number().when('maxAmount', ([maxAmount], schema) =>
              maxAmount
                ? schema.optional().nullable()
                : schema.required(tCommon('errors.required')),
            ),
            maxAmount: Yup.number().nullable().optional(),
            paymentTypeId: Yup.string().nullable().optional(),
            expiredAt: Yup.string().nullable().optional(),
          }),
        ),
        ...validationUtils.getNaturalNumberRangeValidationSchema(
          'payinOrderMinAmount',
          'payinOrderMaxAmount',
        ),
        maxParallelCustomerOrders:
          validationUtils.getNaturalNumberFieldValidationSchema({
            integer: true,
            additionalSchema: Yup.number().max(
              MAX_PARALLEL_CUSTOMER_ORDERS_LIMIT,
              `${tCommon('errors.max')} ${MAX_PARALLEL_CUSTOMER_ORDERS_LIMIT}`,
            ),
          }),
        payinCreateResponseTimeout: Yup.number().optional().nullable(),
        payinCreateSyncRequisitesResponseTimeout: Yup.number()
          .optional()
          .nullable(),
        payinTSBPFallback: Yup.boolean().nullable(),
        payinTCard2CardFallback: Yup.boolean().nullable(),
        payinTSimFallback: Yup.boolean().nullable(),
        payinTSBPNSPKTransform: Yup.boolean().nullable(),
        payinTCard2CardNSPKTransform: Yup.boolean().nullable(),
        payinTNSPKFallback: Yup.boolean().nullable(),
        payinNSPKInstantSyncRequisites: Yup.boolean().nullable(),
        useProviderPaymentUrl: Yup.boolean().nullable(),
        fullWebhook: Yup.boolean().nullable(),
        payinTrustedCustomersOnly: Yup.boolean().nullable(),
        configs: Yup.array().of(
          Yup.object().shape({
            paymentTypeId: Yup.string().required(tCommon('errors.required')),
          }),
        ),
        payinBlockCustomersConfigs: Yup.array().of(
          Yup.object().shape({
            period: Yup.number()
              .moreThan(0, tCommon('errors.natural_number'))
              .required(tCommon('errors.required')),
            count: Yup.number()
              .moreThan(0, tCommon('errors.natural_number'))
              .required(tCommon('errors.required')),
            duration: Yup.number()
              .moreThan(0, tCommon('errors.natural_number'))
              .required(tCommon('errors.required')),
          }),
        ),
        payinBlockAmountConfigs: Yup.array().of(
          Yup.object().shape({
            period: Yup.number()
              .moreThan(0, tCommon('errors.natural_number'))
              .required(tCommon('errors.required')),
            count: Yup.number()
              .moreThan(0, tCommon('errors.natural_number'))
              .required(tCommon('errors.required')),
            duration: Yup.number()
              .moreThan(0, tCommon('errors.natural_number'))
              .required(tCommon('errors.required')),
          }),
        ),
      }),
    [tCommon, isAdminOrTechOperator],
  );

  const handleFormSubmit = useCallback(
    (values: PayinValues, formikHelpers: FormikHelpers<PayinValues>) => {
      const omitFields: string[] = [];
      if (isNew) {
        omitFields.push('payinGroupsConfigs');
      }
      submitShop(values, formikHelpers, omitFields);
    },
    [isNew, submitShop],
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    validateOnMount: true,
    onSubmit: handleFormSubmit,
  });

  useUnsavedChangesGuard(formik);

  const shopFiatCurrencyCode = useMemo(
    () => getFiatCurrencyCode(shopData?.fiatCurrencyId || ''),
    [shopData?.fiatCurrencyId, getFiatCurrencyCode],
  );

  const crossFiatCurrencyIds = useMemo(
    () =>
      compact(
        map(
          formik.values.payinFiatCurrencyExchangeConfigs,
          (config) =>
            find(crossFiatCurrencyExchange, {
              id: config.fiatCurrencyExchangeId,
            })?.crossFiatCurrencyId!,
        ),
      ),
    [formik.values.payinFiatCurrencyExchangeConfigs, crossFiatCurrencyExchange],
  );

  const renderPayinTimeoutInput = useCallback(
    (status: PayinOrderTimeoutConfigKey) => (
      <FormikNumericField
        name={`payinTimeoutConfig.${status}`}
        label={shopUtils.getPayinOrderTimeoutLabel(status)}
        allowNegative={false}
        decimalScale={0}
        disabled={!isAdminOrTechOperator}
      />
    ),
    [isAdminOrTechOperator],
  );

  const isPayinAmountRandomizerPercentageMode = useMemo(
    () =>
      formik.values.payinAmountRandomizer?.mode ===
      AmountRandomizerMode.Percentage,
    [formik.values.payinAmountRandomizer?.mode],
  );

  const toggleAmountRandomizerMode = useCallback(() => {
    if (isPayinAmountRandomizerPercentageMode) {
      formik.setFieldValue(
        'payinAmountRandomizer.mode',
        AmountRandomizerMode.Absolute,
      );
    } else {
      formik.setFieldValue(
        'payinAmountRandomizer.mode',
        AmountRandomizerMode.Percentage,
      );
    }
  }, [formik, isPayinAmountRandomizerPercentageMode]);

  const payinAmountRandomizerBoundsSuffix = useMemo(
    () => (isPayinAmountRandomizerPercentageMode ? '%' : shopFiatCurrencyCode),
    [isPayinAmountRandomizerPercentageMode, shopFiatCurrencyCode],
  );

  const payinTradeMethods = useMemo(() => {
    const payinTradeMethodIds = map(
      formik.values.payinTradeMethodsConfigs,
      (tradeMethod) => tradeMethod.tradeMethodId,
    );
    return filter(tradeMethods, (tradeMethod) =>
      includes(payinTradeMethodIds, tradeMethod.id),
    );
  }, [formik.values.payinTradeMethodsConfigs, tradeMethods]);

  const isDataLoaded = useMemo(() => !!shopData?.name, [shopData?.name]);

  return (
    <FormikProvider value={formik}>
      <Form>
        {/* 1. Trade Methods */}
        {isAdminOrTechOperator && (
          <ManageShopAccordion
            title={t('sections.trade_methods.title')}
            defaultExpanded
          >
            <FormControls>
              <ShopPayinTradeMethods
                key={isDataLoaded ? 'loaded' : 'empty'}
                fiatCurrencyId={shopData?.fiatCurrencyId || ''}
                crossFiatCurrencyIds={crossFiatCurrencyIds}
                hideTitle
              />
            </FormControls>
          </ManageShopAccordion>
        )}

        {/* 2. Cross-fiat currency exchanges */}
        {isAdminOrTechOperator && (
          <ManageShopAccordion
            title={t('sections.cross_currency_exchanges.title')}
          >
            <FormControls>
              <ShopPayinFiatCurrencyExchangeConfigs
                fiatCurrencyId={shopData?.fiatCurrencyId || ''}
                hideTitle
              />
            </FormControls>
          </ManageShopAccordion>
        )}

        {/* 2. Payin Groups */}
        {isAdminOrTechOperator && !isNew && (
          <ManageShopAccordion title={t('sections.order_routing')}>
            <FormControls>
              <ShopPayinGroups groups={payinGroups} />
            </FormControls>
          </ManageShopAccordion>
        )}

        {/* 3. Amounts */}
        <ManageShopAccordion
          title={t('sections.amounts')}
          defaultExpanded={!isAdminOrTechOperator}
        >
          <FormControls>
            <Divider>{t('sections.payin_order_amount_limits')}</Divider>
            <FormControls row>
              <FormikNumericField
                name="payinOrderMinAmount"
                label={tCommon('common.min')}
                fullWidth
                disabled={!isAdminOrTechOperator}
                suffix={shopFiatCurrencyCode}
                allowNegative={false}
              />
              <FormikNumericField
                name="payinOrderMaxAmount"
                label={tCommon('common.max')}
                fullWidth
                disabled={!isAdminOrTechOperator}
                suffix={shopFiatCurrencyCode}
                allowNegative={false}
              />
            </FormControls>
            {isAdminOrTechOperator && (
              <Fragment>
                <AmountAntiFraud
                  orderType={OrderType.Payin}
                  tradeMethods={payinTradeMethods}
                />
                <Divider sx={{ my: 4 }}>
                  {t('sections.payin_amount_randomizer')}
                </Divider>
                <FormControls sx={{ justifyContent: 'space-between' }} row>
                  <FormikCheckbox
                    name="payinAmountRandomizer.enabled"
                    label={t('fields.payin_amount_randomizer_enabled')}
                  />
                  {formik.values.payinAmountRandomizer?.enabled && (
                    <ButtonGroup sx={{ display: 'flex' }}>
                      <Button
                        variant={
                          isPayinAmountRandomizerPercentageMode
                            ? 'contained'
                            : 'outlined'
                        }
                        onClick={toggleAmountRandomizerMode}
                        sx={{ flex: 1 }}
                      >
                        {'%'}
                      </Button>
                      <Button
                        variant={
                          !isPayinAmountRandomizerPercentageMode
                            ? 'contained'
                            : 'outlined'
                        }
                        onClick={toggleAmountRandomizerMode}
                        sx={{ flex: 1 }}
                      >
                        {shopFiatCurrencyCode}
                      </Button>
                    </ButtonGroup>
                  )}
                </FormControls>
                {formik.values.payinAmountRandomizer?.enabled && (
                  <Fragment>
                    <FormControls sx={{ my: 4 }} row>
                      <FormikNumericField
                        name="payinAmountRandomizer.minDivergence"
                        label={t('fields.min_divergence')}
                        fullWidth
                        suffix={payinAmountRandomizerBoundsSuffix}
                      />
                      <FormikNumericField
                        name="payinAmountRandomizer.maxDivergence"
                        label={t('fields.max_divergence')}
                        fullWidth
                        suffix={payinAmountRandomizerBoundsSuffix}
                      />
                      <FormikNumericField
                        name="payinAmountRandomizer.step"
                        label={t('fields.payin_amount_randomizer_step')}
                        fullWidth
                        suffix={payinAmountRandomizerBoundsSuffix}
                      />
                    </FormControls>
                    <FormControls sx={{ my: 4 }} row>
                      <FormikNumericField
                        name="payinAmountRandomizer.minOrderAmount"
                        label={t('fields.min_order_amount')}
                        fullWidth
                        suffix={shopFiatCurrencyCode}
                      />
                      <FormikNumericField
                        name="payinAmountRandomizer.maxOrderAmount"
                        label={t('fields.max_order_amount')}
                        fullWidth
                        suffix={shopFiatCurrencyCode}
                      />
                    </FormControls>
                  </Fragment>
                )}
                <PayinBlockAmounts />
              </Fragment>
            )}
          </FormControls>
        </ManageShopAccordion>

        {/* 4. Timeouts */}
        <ManageShopAccordion title={t('sections.timeouts')}>
          <FormControls>
            <Typography variant="subtitle2">
              {t('fields.status_timeouts_config')}
            </Typography>
            {renderPayinTimeoutInput(PayinOrderTimeoutConfigKey.New)}
            {renderPayinTimeoutInput(PayinOrderTimeoutConfigKey.Requisites)}
            {renderPayinTimeoutInput(PayinOrderTimeoutConfigKey.Payment)}
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2">
              {t('fields.response_timeouts_config')}
            </Typography>
            <FormikNumericField
              name="payinCreateResponseTimeout"
              label={t('fields.create_order')}
              allowNegative={false}
              decimalScale={0}
              disabled={!isAdminOrTechOperator}
              defaultValue={0}
            />
            <FormikNumericField
              name="payinCreateSyncRequisitesResponseTimeout"
              label={t('fields.create_order')}
              helperText={t('fields.sync_requisites')}
              allowNegative={false}
              decimalScale={0}
              disabled={!isAdminOrTechOperator}
              defaultValue={0}
            />
          </FormControls>
        </ManageShopAccordion>

        {/* 5. Requisites & Distribution */}
        {isAdminOrTechOperator && (
          <ManageShopAccordion
            title={t('sections.requisites_and_distribution')}
          >
            <FormControls>
              <FormControl sx={{ flexDirection: 'row', alignItems: 'center' }}>
                <FormikCheckbox
                  name="payinDistributionSameRequisites"
                  label={t(
                    'fields.restrictions.payin_distribution_same_requisites',
                  )}
                  sx={{ marginRight: 0 }}
                />
                <HelpTooltip
                  title={t(
                    'fields.restrictions.payin_distribution_same_requisites_helper',
                  )}
                />
              </FormControl>
              <FormControl sx={{ flexDirection: 'row', alignItems: 'center' }}>
                <FormikCheckbox
                  name="distributionDebug"
                  label={t('fields.distribution_debug')}
                  sx={{ marginRight: 0 }}
                />
                <HelpTooltip title={t('fields.distribution_debug_helper')} />
              </FormControl>
              <FormControl sx={{ flexDirection: 'row', alignItems: 'center' }}>
                <FormikCheckbox
                  name="payinNSPKInstantSyncRequisites"
                  label={t('fields.payin_nspk_instant_sync_requisites')}
                  sx={{ marginRight: 0 }}
                />
                <HelpTooltip
                  title={t('fields.payin_nspk_instant_sync_requisites_helper')}
                />
              </FormControl>
              <FormControl sx={{ flexDirection: 'row', alignItems: 'center' }}>
                <FormikCheckbox
                  name="useProviderPaymentUrl"
                  label={t('fields.use_provider_payment_url')}
                  sx={{ marginRight: 0 }}
                />
                <HelpTooltip
                  title={t('fields.use_provider_payment_url_helper')}
                />
              </FormControl>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2">
                {t('fields.payin_requisites_fallback')}
                <HelpTooltip
                  title={t('fields.payin_requisites_fallback_description')}
                />
              </Typography>
              <FormControl>
                <FormikCheckbox
                  name="payinTSBPFallback"
                  label={t('fields.payin_tsbp_fallback')}
                />
              </FormControl>
              <FormControl>
                <FormikCheckbox
                  name="payinTCard2CardFallback"
                  label={t('fields.payin_tcard2card_fallback')}
                />
              </FormControl>
              <FormControl>
                <FormikCheckbox
                  name="payinTSimFallback"
                  label={t('fields.payin_tsim_fallback')}
                />
              </FormControl>
              <FormControl>
                <FormikCheckbox
                  name="payinTNSPKFallback"
                  label={t('fields.payin_tnspk_fallback')}
                />
              </FormControl>
              {isMultitransferEnabled && (
                <Fragment>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2">
                    {t('fields.payin_requisites_transform')}
                    <HelpTooltip
                      title={t('fields.payin_requisites_transform_description')}
                    />
                  </Typography>
                  <FormControl>
                    <FormikCheckbox
                      name="payinTSBPNSPKTransform"
                      label={t('fields.payin_tsbp_nspk_transform')}
                    />
                  </FormControl>
                  <FormControl>
                    <FormikCheckbox
                      name="payinTCard2CardNSPKTransform"
                      label={t('fields.payin_tcard2card_nspk_transform')}
                    />
                  </FormControl>
                </Fragment>
              )}
            </FormControls>
          </ManageShopAccordion>
        )}

        {/* 6. Customers */}
        <ManageShopAccordion
          title={t('tabs.customers')}
          defaultExpanded={!isAdminOrTechOperator}
        >
          <FormControls>
            <MaxParallelCustomerOrdersField />
            {isAdminOrTechOperator && (
              <Fragment>
                <FormControl>
                  <FormikCheckbox
                    name="payinTrustedCustomersOnly"
                    label={t('fields.payin_trusted_customers_only')}
                  />
                </FormControl>
                <FormControl>
                  <FormikCheckbox
                    name="fullWebhook"
                    label={t('fields.full_webhook')}
                  />
                </FormControl>
                <Divider />
                <PayinBlockCustomers />
              </Fragment>
            )}
          </FormControls>
        </ManageShopAccordion>

        <ManageShopTabActions />
      </Form>
    </FormikProvider>
  );
};
